import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { SerializedEditorState } from 'lexical';
import { useCallback } from 'react';
import { LoadingId } from '../../types';
import { useLoadingBarState } from '../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { INoteDataModel, INoteDraftDataModel } from '../../data-models/note.data-model';
import { selectedNoteStateJ } from '../../pages/CompanyProfiles/state/UIStateJ';
import { createNote, deleteNote, updateNote } from '../queries/MaggieNoteQueries';
import { notesStateJ } from '../state/NoteStateJ';

export function useCreateNote() {
  const { pushErrorToast } = useToastMessageState();

  // Generic preserved by typing get/set explicitly; useAtomCallback can't infer
  // the generic itself, so we type the inner callback by hand.
  const createNoteCallback = useCallback(
    async <T>(get: Getter, set: Setter, companyId: number, newNoteData: INoteDraftDataModel<T>) => {
      const currentNotes = await get(notesStateJ(companyId));
      try {
        const selectedNote = get(selectedNoteStateJ);
        const response = await createNote<T>(newNoteData);
        if (selectedNote?.id === -1) {
          set(selectedNoteStateJ, response.note as INoteDataModel<SerializedEditorState>);
        }
        set(notesStateJ(companyId), [response.note, ...currentNotes]);

        return response.note;
      } catch (err) {
        set(notesStateJ(companyId), currentNotes);
        pushErrorToast({ message: 'Failed to create note' });
      }
    },
    [pushErrorToast]
  );

  return useAtomCallback(createNoteCallback);
}

export function useUpdateNote() {
  const { pushErrorToast } = useToastMessageState();

  const updateNoteCallback = useCallback(
    async <T>(get: Getter, set: Setter, companyId: number, updatedNote: INoteDataModel<T>) => {
      const selectedNote = get(selectedNoteStateJ);
      const currentNotes = await get(notesStateJ(companyId));

      // note might have been deleted in the meantime
      if (!currentNotes.find((note) => note.id === updatedNote.id)) {
        return;
      }

      try {
        const response = await updateNote(updatedNote);

        set(
          notesStateJ(companyId),
          currentNotes.map((note) => (note.id === response.note.id ? response.note : note))
        );

        if (selectedNote?.id === updatedNote.id) {
          set(selectedNoteStateJ, updatedNote as INoteDataModel<SerializedEditorState>);
        }

        return response.note;
      } catch (err) {
        set(notesStateJ(companyId), currentNotes);
        pushErrorToast({ message: 'Failed to update note' });
      }
    },
    [pushErrorToast]
  );

  return useAtomCallback(updateNoteCallback);
}

export function useDeleteNote() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (get, set, companyId: number, noteId: number) => {
        actions.startLoading(LoadingId.deleteNote);
        const currentNotes = await get(notesStateJ(companyId));
        const notesWithoutDeletedOne = currentNotes.filter((note) => note.id !== noteId);

        try {
          await deleteNote(noteId);
          set(notesStateJ(companyId), notesWithoutDeletedOne);
          pushSuccessToast({ message: 'Note deleted' });
          return true;
        } catch (err) {
          set(notesStateJ(companyId), currentNotes);
          pushErrorToast({ message: 'Failed to delete note' });
          return false;
        } finally {
          actions.stopLoading(LoadingId.deleteNote);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );
}
